import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useDropzone } from 'react-dropzone';
import { ContentState, EditorState } from 'draft-js';
import { convertFromHTML } from 'draft-convert';
import RichTextEditor from 'draft-js-plugins-editor';
import { stateToHTML } from 'draft-js-export-html';
import createDndFileUploadPlugin from '@draft-js-plugins/drag-n-drop-upload';
import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import createLinkPlugin from '@draft-js-plugins/anchor';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  HeadlineOneButton,
  HeadlineTwoButton,
  UnorderedListButton,
  BlockquoteButton,
} from '@draft-js-plugins/buttons';
import 'draft-js/dist/Draft.css';

import {
  Form,
  Input,
  Button,
  Editor,
  LoadingSpinner,
  Checkbox,
} from '../_shared';
import { uploadToS3, isUntrustedVideoScript } from '../../utils/helper';
import { ToolbarContainer } from './PostForm.styled';
import { makeSelectUser } from '../../store/selectors';

const toolbarConfig = {
  // Optionally specify the groups to display (displayed in the order listed).
  display: [
    'INLINE_STYLE_BUTTONS',
    'BLOCK_TYPE_BUTTONS',
    'LINK_BUTTONS',
    'HISTORY_BUTTONS',
  ],
  INLINE_STYLE_BUTTONS: [
    { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' },
  ],
};

const IconButton = styled.button.attrs({
  className: 'text-base p-1 mr-3',
})``;

const PostSchema = object().shape({
  title: string()
    .trim()
    .required('Title is required.')
    .max(75, 'Title must be less than 75 chars.'),
});

const linkPlugin = createLinkPlugin();
const dndFileUploadPlugin = createDndFileUploadPlugin();
const staticToolbarPlugin = createToolbarPlugin();
const { Toolbar } = staticToolbarPlugin;
const plugins = [staticToolbarPlugin, dndFileUploadPlugin, linkPlugin];

const PostForm = (props) => {
  const [error, setError] = useState(props.error);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [hasVideo, setHasVideo] = useState(false);
  const [video, setVideo] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const imgRef = useRef();
  const user = useSelector(makeSelectUser());

  const isInvalidVideo = useMemo(() => isUntrustedVideoScript(video), [video]);

  useEffect(() => {
    if (props.defaultValues && Object.keys(props.defaultValues).length) {
      const blocksFromHTML = convertFromHTML(props.defaultValues.content);
      // const state = ContentState.createFromBlockArray(
      //   blocksFromHTML.contentBlocks,
      //   blocksFromHTML.entityMap
      // );
      setEditorState(EditorState.createWithContent(blocksFromHTML));
    }
  }, [props.defaultValues]);

  useEffect(() => setError(props.error), [props.error]);

  const handleEditorChange = (es) => {
    setEditorState(es);
  };
  // const handleSubmit = async (data) => {
  //   if (isInvalidVideo) {
  //     alert('Invalid or untrusted video.');
  //     return;
  //   }

  //   const filesResult = [];

  //   if (uploadFiles.length) {
  //     setUploadingStatus(
  //       uploadFiles.reduce(
  //         (prev, cur) => ({ ...prev, [cur.name]: 'reading' }),
  //         {}
  //       )
  //     );

  //     const xlsxExt = 'vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  //     const res = await Promise.all(
  //       uploadFiles.map((file) =>
  //         uploadToS3(file, '', file.type.indexOf(xlsxExt) > -1 ? '.xlsx' : '')
  //       )
  //     );

  //     const result = {};
  //     for (let i = 0; i < uploadFiles.length; i += 1) {
  //       if (res[i].status === 204) {
  //         filesResult.push(
  //           res[i].key
  //             .split('/')[1]
  //             .replace(new RegExp(String.raw`${xlsxExt}`, 'g'), 'xlsx')
  //         );
  //         result[uploadFiles[i].name] = 'success';
  //       } else {
  //         result[uploadFiles[i].name] = 'error';
  //       }
  //     }

  //     setUploadingStatus(result);
  //   }

  //   const payload = {
  //     ...data,
  //     isPrivate,
  //     video: hasVideo ? video : null,
  //     content: stateToHTML(editorState.getCurrentContent()),
  //     attachments: filesResult,
  //   };

  //   props.onSubmit(payload, () => {
  //     setEditorState(EditorState.createEmpty());
  //   });
  // };

  const handleSubmit = async (data) => {
    if (isInvalidVideo) {
      alert('Invalid or untrusted video.');
      return;
    }

    const filesResult = [];

    if (uploadFiles.length) {
      setUploadingStatus(
        uploadFiles.reduce(
          (prev, cur) => ({ ...prev, [cur.name]: 'reading' }),
          {}
        )
      );

      const mimeToExtension = {
        'vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'vnd.ms-excel': '.xls',
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-powerpoint': '.ppt',
        'vnd.openxmlformats-officedocument.presentationml.presentation':
          '.pptx',
        'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
        // ... other MIME types
      };

      console.log('Called');

      // Helper function to get filename without its extension
      const getFileNameWithoutExtension = (filename) => {
        return filename.slice(0, filename.lastIndexOf('.')) || filename;
      };

      const res = await Promise.all(
        uploadFiles.map((file) => {
          const extension =
            mimeToExtension[file.type] ||
            file.name.slice(file.name.lastIndexOf('.'));
          const originalFilenameWithoutExtension = getFileNameWithoutExtension(
            file.name
          );
          const newFilename = originalFilenameWithoutExtension + extension;

          // Pass the modified filename to uploadToS3
          return uploadToS3(file, '', newFilename);
        })
      );

      const result = {};
      for (let i = 0; i < uploadFiles.length; i += 1) {
        if (res[i].status === 204) {
          filesResult.push(res[i].key.split('/')[1]);
          result[uploadFiles[i].name] = 'success';
        } else {
          result[uploadFiles[i].name] = 'error';
        }
      }

      setUploadingStatus(result);
    }

    const payload = {
      ...data,
      isPrivate,
      video: hasVideo ? video : null,
      content: stateToHTML(editorState.getCurrentContent()),
      attachments: filesResult,
    };

    props.onSubmit(payload, () => {
      setEditorState(EditorState.createEmpty());
    });
  };

  const handleDropFiles = (files) => setUploadFiles(files);
  const handleRemoveFile = (name) => () => {
    setUploadFiles(uploadFiles.filter((file) => file.name !== name));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDropFiles,
  });

  return (
    <Form
      schema={PostSchema}
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="text-red-100 text-xs mb-4 w-full text-center">
          {error.charAt(0).toUpperCase() + error.slice(1)}
        </div>
      )}
      <div className="mb-7 sm:mb-4 pt-2 px-2">
        <Input
          id="title"
          name="title"
          placeholder="Post a new topic"
          type="text"
          autoComplete="off"
          required
        />
      </div>
      <Editor>
        <RichTextEditor
          className="main-editor max-h-150 min-h-32 overflow-auto"
          editorState={editorState}
          toolbarConfig={toolbarConfig}
          spellCheck={true}
          plugins={plugins}
          onChange={handleEditorChange}
        />
        {hasVideo && (
          <div className="mt-4">
            <p className="text-sm font-semibold">Video Script</p>
            <textarea
              placeholder="Add video script here..."
              rows="2"
              className="video-script p-2 text-sm"
              value={video}
              onChange={(e) => setVideo(e.target.value)}
            />
            {isInvalidVideo && (
              <span className="text-sm text-red">
                Invalid or untrusted video script.
              </span>
            )}
          </div>
        )}
        <div
          {...getRootProps({
            className:
              'cursor-pointer text-center p-2 mt-4 border-2 border-dashed border-gray-800 text-gray-800 hover:text-gray-200 hover:border-gray-200 w-full',
          })}
        >
          <input {...getInputProps()} />
          <p>Drag and drop files here or click to select file</p>
        </div>
        <div className="flex flex-col items-start">
          {uploadFiles.map((file) => (
            <div
              className="file-button flex items-center bg-gray-800 p-1 mt-1"
              key={file.name}
            >
              {file.name}
              <div className="status ml-4">
                {uploadingStatus[file.name] === 'reading' ? (
                  <LoadingSpinner size="16px" />
                ) : uploadingStatus[file.name] === 'error' ? (
                  <span className="text-red text-tinay">Error!</span>
                ) : (
                  <span role="button" onClick={handleRemoveFile(file.name)}>
                    X
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <ToolbarContainer>
          <Toolbar className="toolbar">
            {
              // may be use React.Fragment instead of div to improve perfomance after React 16
              (externalProps) => (
                <div className="toolbar flex items-start h-6 flex-grow">
                  <BoldButton {...externalProps} />
                  <ItalicButton {...externalProps} />
                  <UnderlineButton {...externalProps} />
                  <UnorderedListButton {...externalProps} />
                  <HeadlineOneButton {...externalProps} />
                  <HeadlineTwoButton {...externalProps} />
                  <BlockquoteButton {...externalProps} />
                  <linkPlugin.LinkButton {...externalProps} />
                  <button
                    type="button"
                    className="text-sm p-2 add-video-btn"
                    onClick={() => {
                      setVideo('');
                      setHasVideo((v) => !v);
                    }}
                  >
                    {hasVideo ? 'Remove Video' : 'Attach Video'}
                  </button>
                </div>
              )
            }
          </Toolbar>
          <div className="flex items-center">
            <div className="mr-3">
              <Checkbox
                checked={isPrivate}
                label="Mark as private"
                onClick={() => setIsPrivate((v) => !v)}
              />
            </div>
            {typeof props.onCancel === 'function' && (
              <Button
                className="mr-2 font-semibold px-4 py-2 text-base complete-btn uppercase bg-primary font-bold text-white rounded sm:w-full sm:mt-4"
                caption="Cancel"
                type="neutral"
                onClick={props.onCancel}
              />
            )}
            <Button
              className="font-semibold px-4 py-2 text-base complete-btn uppercase bg-primary font-bold text-white rounded sm:w-full sm:mt-4"
              caption={props.busy ? 'Submitting...' : 'Post'}
              type="misc"
            />
          </div>
        </ToolbarContainer>
      </Editor>
    </Form>
  );
};

export default PostForm;
